import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Construction, MoreHorizontal, Edit, Trash2, CheckCircle, Search, AlertTriangle, RotateCcw, PlayCircle, PauseCircle, Palette, FileText, Pin, Hand, FastForward, Flame, Download, ChevronUp, ChevronDown, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getProjectColor } from '../utils/colors';
import PlanningAlerts from './PlanningAlerts';
import ColorChoiceDialog from './ColorChoiceDialog';
import DocumentDialog from './DocumentDialog';
import usePlanningDiagnostics from '../hooks/usePlanningDiagnostics';
import { projectAPI } from '../services/api';

const STATUS_LABEL = { planned: 'Planifié', 'in-progress': 'En cours', completed: 'Terminé', paused: 'En pause' };
const STATUS_STYLE = {
  planned: 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-700',
  paused: 'bg-yellow-100 text-yellow-800',
};
const STATUS_ORDER = { 'in-progress': 0, planned: 1, paused: 2, completed: 3 };

const shortDate = (d) => (d ? format(d instanceof Date ? d : parseISO(d), 'dd/MM/yy', { locale: fr }) : '—');
const pct = (v, signed = false) => (v == null ? '—'
  : `${signed && v > 0 ? '+' : ''}${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);
const euros = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('fr-FR')} €`);
// Recherche insensible aux accents : « primeveres » trouve « Les primevères »
const norm = (s) => (s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

// Colonnes du tableau (écran large). Sur téléphone, chaque ligne se replie en
// carte de deux lignes : nom en haut, statut/dates/heures/équipe en dessous.
const GRID = 'md:grid-cols-[minmax(0,1fr)_92px_150px_128px_150px_32px]';

const AllProjectsView = ({
  projects, employees, onEditProject, onDeleteProject, onCompleteProject,
  onPauseProject, onResumeProject,
  pendingQuotes, isSyncingPennylane, onFetchPennylaneQuotes, onCreateProjectFromQuote, onDismissQuotes,
}) => {
  const diagnostics = usePlanningDiagnostics(projects, employees);
  const { alertsByProject, projectEnd } = diagnostics;

  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [colorChoiceProject, setColorChoiceProject] = useState(null);
  const [documentsProject, setDocumentsProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('active');
  const [sort, setSort] = useState({ key: 'start', dir: 1 });
  const [financials, setFinancials] = useState(null); // { configured, items: { projectId: bilan } }
  const [financialsError, setFinancialsError] = useState(false);
  const completedView = filter === 'completed';

  // Bilan des chantiers terminés (écart d'heures, marge réelle) : calculé par l'app à chaque
  // ouverture de la vue « Terminés » — il connaît le coût réel de chaque heure pointée.
  useEffect(() => {
    if (!completedView) return undefined;
    let cancelled = false;
    setFinancialsError(false);
    projectAPI.planningFinancials()
      .then((data) => { if (!cancelled) setFinancials(data); })
      .catch(() => { if (!cancelled) setFinancialsError(true); });
    return () => { cancelled = true; };
  }, [completedView]);

  const changeFilter = (key) => {
    setFilter(key);
    // Terminés : les plus récents d'abord. Ailleurs, tri par démarrage.
    if (key === 'completed') setSort({ key: 'end', dir: -1 });
    else if (['end', 'gap', 'margin'].includes(sort.key)) setSort({ key: 'start', dir: 1 });
  };

  const bilan = (p) => financials?.items?.[p.id] || null;

  const handleDeleteProject = () => {
    if (deleteProjectId) {
      onDeleteProject(deleteProjectId);
      setDeleteProjectId(null);
    }
  };

  const getEmployeeById = (id) => employees.find((emp) => emp.id === id);

  const filters = [
    { key: 'active', label: 'Actifs', test: (p) => p.status !== 'completed' },
    { key: 'in-progress', label: 'En cours', test: (p) => p.status === 'in-progress' },
    { key: 'planned', label: 'Planifiés', test: (p) => p.status === 'planned' },
    { key: 'paused', label: 'En pause', test: (p) => p.status === 'paused' },
    { key: 'alert', label: 'Avec alerte', test: (p) => p.status !== 'completed' && !!alertsByProject[p.id] },
    { key: 'completed', label: 'Terminés', test: (p) => p.status === 'completed' },
  ];
  const activeFilter = filters.find((f) => f.key === filter) || filters[0];

  const endOf = (p) => (p.status === 'completed'
    ? (p.actualEndDate ? parseISO(p.actualEndDate) : null)
    : projectEnd(p));

  const rows = useMemo(() => {
    const q = norm(searchTerm);
    const sortValue = (p) => {
      switch (sort.key) {
        case 'name': return norm(p.name);
        case 'status': return STATUS_ORDER[p.status] ?? 9;
        case 'end': { const e = endOf(p); return e ? e.getTime() : -Infinity; }
        case 'hours': return p.hoursToSell || 0;
        case 'gap': { const g = bilan(p)?.hoursGapPct; return g == null ? Infinity * sort.dir : g; }
        case 'margin': { const m = bilan(p)?.marginPct; return m == null ? Infinity * sort.dir : m; }
        default: return p.actualStartDate ? parseISO(p.actualStartDate).getTime() : Infinity;
      }
    };
    return projects
      .filter(activeFilter.test)
      .filter((p) => !q || norm(p.name).includes(q) || norm(p.clientName).includes(q) || norm(p.clientAddress).includes(q))
      .sort((a, b) => {
        const va = sortValue(a);
        const vb = sortValue(b);
        if (va === vb) return norm(a.name).localeCompare(norm(b.name));
        if (typeof va === 'string') return va.localeCompare(vb) * sort.dir;
        return (va < vb ? -1 : 1) * sort.dir;
      });
    // eslint-disable-next-line
  }, [projects, searchTerm, filter, sort, alertsByProject, financials]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const SortHeader = ({ k, children, className = '' }) => (
    <button type="button" onClick={() => toggleSort(k)}
            className={`flex items-center gap-1 hover:text-gray-900 ${sort.key === k ? 'text-gray-900' : ''} ${className}`}>
      {children}
      {sort.key === k && (sort.dir === 1 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
    </button>
  );

  const teamCell = (project) => {
    // En pause : l'équipe a été démobilisée, on montre celle d'avant la pause
    const paused = project.status === 'paused';
    const ids = paused ? (project.teamBeforePause || []) : (project.assignedEmployees || []);
    const managerId = paused ? project.siteManagerBeforePause : project.siteManagerId;
    const members = ids.map(getEmployeeById).filter(Boolean)
      .sort((a, b) => (b.id === managerId) - (a.id === managerId));
    if (!members.length) return <span className="text-gray-400">—</span>;
    const full = members.map((m) => `${m.fullName}${m.id === managerId ? ' (responsable)' : ''}`).join(', ');
    const text = members.length > 2
      ? `${members[0].firstName}${members[0].id === managerId ? '★' : ''} +${members.length - 1}`
      : members.map((m) => `${m.firstName}${m.id === managerId ? '★' : ''}`).join(', ');
    return (
      <span className={`truncate ${paused ? 'text-gray-400 italic' : 'text-gray-700'}`}
            title={paused ? `Équipe avant la pause : ${full}` : full}>
        {text}
      </span>
    );
  };

  const hoursCell = (project) => {
    const sold = project.hoursToSell || 0;
    const done = project.actualHoursWorked || 0;
    if ((project.status === 'in-progress' || project.status === 'paused') && sold && done) {
      const pct = Math.min(100, (done / sold) * 100);
      return (
        <div className="min-w-0" title={`${Math.round(done)} h faites sur ${sold} h vendues`}>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-1.5 ${project.status === 'paused' ? 'bg-yellow-400' : 'bg-green-500'}`}
                 style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-gray-600 tabular-nums">{Math.round(done)} / {sold} h</div>
        </div>
      );
    }
    return <span className="text-gray-700 tabular-nums">{sold ? `${sold} h` : '—'}</span>;
  };

  // --- Vue « Terminés » : bilan lu dans le classeur ---
  const gapCell = (project) => {
    const b = bilan(project);
    if (!b || b.hoursGapPct == null) return <span className="text-gray-400">{financials ? '—' : '…'}</span>;
    const g = b.hoursGapPct;
    const tone = b.overrun ? 'bg-red-100 text-red-800' : g > 0 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800';
    const planned = b.actualHours != null && g !== -1 ? b.actualHours / (1 + g) : null;
    const diff = planned != null ? b.actualHours - planned : null;
    return (
      <div className="min-w-0" title={planned != null
        ? `${Math.round(b.actualHours)} h réelles pour ${Math.round(planned)} h prévues${b.invoiced ? ' (sur le facturé)' : ' (sur le devis)'}${b.overrun ? ' — dépassement du seuil' : ''}`
        : ''}>
        <span className={`inline-block px-1.5 rounded text-xs font-medium tabular-nums ${tone}`}>{pct(g, true)}</span>
        {diff != null && (
          <div className="text-xs text-gray-600 tabular-nums">
            {diff > 0 ? '+' : ''}{Math.round(diff)} h · {Math.round(b.actualHours)} h faites
          </div>
        )}
        {b.partiallyInvoiced && (
          <div className="text-xs text-amber-700 flex items-center gap-1"
               title={`Seul ${euros(b.invoicedHT)} est facturé sur ${euros(b.quoteHT)} de devis : l'écart est calculé sur ce montant, pas sur le chantier entier.`}>
            <AlertTriangle className="w-3 h-3 shrink-0" /> facturation partielle
          </div>
        )}
      </div>
    );
  };

  const marginCell = (project) => {
    const b = bilan(project);
    if (!b || b.marginPct == null) return <span className="text-gray-400">{financials ? '—' : '…'}</span>;
    const delta = b.estimatedMarginPct != null ? (b.marginPct - b.estimatedMarginPct) * 100 : null;
    return (
      <div className="min-w-0" title={`Marge réelle ${pct(b.marginPct)} — estimée au devis ${pct(b.estimatedMarginPct)}${b.invoiced ? ', calculée sur le facturé' : ', calculée sur le devis (pas encore facturé)'}`}>
        <span className={`font-medium tabular-nums ${b.marginPct < 0 ? 'text-red-700' : 'text-gray-900'}`}>{pct(b.marginPct)}</span>
        <span className="text-xs text-gray-500 tabular-nums"> · {euros(b.marginAmount)}</span>
        {b.partiallyInvoiced && (
          <div className="text-xs text-amber-700">
            reste {euros(b.remainingHT)} à facturer
          </div>
        )}
        {delta != null && Math.abs(delta) >= 0.5 && (
          <div className={`text-xs tabular-nums ${delta < 0 ? 'text-orange-700' : 'text-green-700'}`}>
            {delta > 0 ? '+' : '−'}{Math.abs(delta).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} pts vs devis
          </div>
        )}
      </div>
    );
  };

  // Bilan cumulé des chantiers terminés affichés (pondéré, pas une moyenne
  // de pourcentages : un gros chantier pèse plus qu'une retouche).
  const completedSummary = (() => {
    if (!completedView || !financials?.items) return null;
    let revenue = 0, margin = 0, actual = 0, planned = 0, n = 0;
    rows.forEach((p) => {
      const b = bilan(p);
      if (!b) return;
      n += 1;
      if (b.revenueHT && b.marginAmount != null) { revenue += b.revenueHT; margin += b.marginAmount; }
      if (b.actualHours != null && b.hoursGapPct != null && b.hoursGapPct !== -1) {
        actual += b.actualHours;
        planned += b.actualHours / (1 + b.hoursGapPct);
      }
    });
    if (!n) return null;
    return { n, revenue, margin, actual, planned };
  })();

  const datesCell = (project) => {
    if (project.status === 'paused') {
      return <span className="text-yellow-700">en attente de reprise</span>;
    }
    if (project.status === 'completed') {
      return <span className="text-gray-700 tabular-nums">{project.actualEndDate ? shortDate(project.actualEndDate) : '—'}</span>;
    }
    if (!project.actualStartDate) return <span className="text-gray-400">non planifié</span>;
    return (
      <span className="text-gray-700 tabular-nums">
        {shortDate(project.actualStartDate)} → {shortDate(projectEnd(project))}
      </span>
    );
  };

  const markers = (project) => {
    const projectAlerts = alertsByProject[project.id] || [];
    const worst = projectAlerts.some((a) => a.level === 'danger') ? 'danger'
      : projectAlerts.some((a) => a.level === 'warn') ? 'warn' : projectAlerts.length ? 'info' : null;
    return (
      <span className="inline-flex items-center gap-1 shrink-0">
        {project.isUrgent && <Flame className="w-3.5 h-3.5 text-red-500" aria-label="Urgent" />}
        {project.hasFixedStartDate && <Pin className="w-3.5 h-3.5 text-red-500" aria-label="Date imposée" />}
        {project.manuallyPlaced && !project.hasFixedStartDate && (
          <Hand className="w-3.5 h-3.5 text-gray-500" aria-label="Posé à la main" />
        )}
        {project.advancedFromMonth && (
          <FastForward className="w-3.5 h-3.5 text-blue-500" aria-label="Avancé avant son mois souhaité" />
        )}
        {project.isComplex && (
          <span className="text-[10px] leading-4 px-1 rounded bg-orange-100 text-orange-700">complexe</span>
        )}
        {worst && worst !== 'info' && (
          <span title={projectAlerts.map((a) => `${a.label} : ${a.detail}`).join('\n')}>
            <AlertTriangle className={`w-3.5 h-3.5 ${worst === 'danger' ? 'text-red-600' : 'text-orange-500'}`}
                           aria-label="Alerte" />
          </span>
        )}
      </span>
    );
  };

  const rowMenu = (project) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => e.stopPropagation()}
                aria-label={`Actions sur ${project.name}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* Les événements React traversent le portail du menu : sans ce
          stopPropagation, choisir une action ouvrait aussi la fiche. */}
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => onEditProject(project)}>
          <Edit className="w-4 h-4 mr-2" />
          Modifier
        </DropdownMenuItem>
        {project.status === 'completed' ? (
          <DropdownMenuItem onClick={() => onCompleteProject({ ...project, status: 'planned', actualEndDate: null })}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Réactiver
          </DropdownMenuItem>
        ) : project.status === 'paused' ? (
          <DropdownMenuItem onClick={() => onResumeProject(project)}>
            <PlayCircle className="w-4 h-4 mr-2" />
            Reprendre
          </DropdownMenuItem>
        ) : (
          <>
            {project.status === 'planned' && (
              <DropdownMenuItem onClick={() => onCompleteProject({ ...project, status: 'in-progress' })}>
                <PlayCircle className="w-4 h-4 mr-2" />
                Marquer en cours
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onPauseProject(project)}>
              <PauseCircle className="w-4 h-4 mr-2" />
              Mettre en pause
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onCompleteProject({ ...project, status: 'completed' })}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Marquer terminé
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem onClick={() => setColorChoiceProject(project)}>
          <Palette className="w-4 h-4 mr-2" />
          Choix couleur
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setDocumentsProject(project)}>
          <FileText className="w-4 h-4 mr-2" />
          Documents
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setDeleteProjectId(project.id)} className="text-red-600 hover:text-red-700">
          <Trash2 className="w-4 h-4 mr-2" />
          Supprimer
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-4">
      {/* Pilotage : quatre indicateurs, détail à la demande */}
      <PlanningAlerts projects={projects} employees={employees} onEditProject={onEditProject}
                      diagnostics={diagnostics} />

      {/* Barre d'outils : recherche, filtres, imports */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input placeholder="Chantier, client ou adresse" value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filters.map((f) => {
            const count = projects.filter(f.test).length;
            const selected = filter === f.key;
            return (
              <button key={f.key} type="button" onClick={() => changeFilter(f.key)} aria-pressed={selected}
                      className={`px-2.5 py-1 rounded-full text-sm border transition-colors ${
                        selected ? 'bg-green-600 border-green-600 text-white'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                {f.label} <span className={selected ? 'text-green-100' : 'text-gray-400'}>{count}</span>
              </button>
            );
          })}
        </div>
        <Button variant="outline" size="sm" onClick={onFetchPennylaneQuotes} disabled={isSyncingPennylane}
                className="border-green-200 text-green-700 hover:bg-green-50"
                title="Chercher les devis acceptés dans Pennylane qui ne sont pas encore des chantiers">
          <Download className={`w-4 h-4 mr-1.5 ${isSyncingPennylane ? 'animate-pulse' : ''}`} />
          {isSyncingPennylane ? 'Recherche…' : 'Devis Pennylane'}
        </Button>
      </div>

      {/* Devis Pennylane à importer : n'apparaît que s'il y en a */}
      {pendingQuotes && pendingQuotes.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-medium text-green-800">
              {pendingQuotes.length} devis accepté(s) à transformer en chantier
            </div>
            {onDismissQuotes && (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-green-700" onClick={onDismissQuotes}
                      aria-label="Masquer les devis">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="divide-y divide-green-100">
            {pendingQuotes.map((quote) => (
              <div key={quote.id} className="flex items-center justify-between py-2 gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 truncate">
                    {quote.clientName || 'Client inconnu'}{quote.quoteNumber ? ` — ${quote.quoteNumber}` : ''}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {quote.clientAddress || 'Adresse non renseignée'}
                    {quote.quoteAmountHT != null && ` · ${quote.quoteAmountHT.toLocaleString('fr-FR')}€ HT`}
                    {quote.hoursToSell != null && ` · ~${quote.hoursToSell}h pondérées estimées`}
                  </div>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                        onClick={() => onCreateProjectFromQuote(quote)}>
                  Créer le chantier
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bilan cumulé des chantiers terminés */}
      {completedView && financialsError && (
        <p className="text-sm text-orange-700">Bilan illisible : le serveur n'a pas pu calculer les marges.</p>
      )}
      {completedSummary && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
          <span>
            Marge réelle cumulée{' '}
            <span className="font-semibold text-gray-900">{euros(completedSummary.margin)}</span>
            {completedSummary.revenue > 0 && (
              <> soit <span className="font-semibold text-gray-900">{pct(completedSummary.margin / completedSummary.revenue)}</span> du chiffre d'affaires</>
            )}
          </span>
          {completedSummary.planned > 0 && (
            <span>
              Heures : {Math.round(completedSummary.actual).toLocaleString('fr-FR')} h faites pour{' '}
              {Math.round(completedSummary.planned).toLocaleString('fr-FR')} h prévues{' '}
              <span className={`font-semibold ${completedSummary.actual > completedSummary.planned ? 'text-orange-700' : 'text-green-700'}`}>
                ({pct(completedSummary.actual / completedSummary.planned - 1, true)})
              </span>
            </span>
          )}
          <span className="text-gray-500">sur {completedSummary.n} chantier(s) affiché(s)</span>
        </div>
      )}

      {/* Liste des chantiers */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className={`hidden md:grid ${GRID} gap-x-3 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500`}>
          <SortHeader k="name">Chantier</SortHeader>
          <SortHeader k="status">Statut</SortHeader>
          {completedView ? <SortHeader k="end">Terminé le</SortHeader> : <SortHeader k="start">Début → fin</SortHeader>}
          {completedView ? <SortHeader k="gap">Écart heures</SortHeader> : <SortHeader k="hours">Heures</SortHeader>}
          {completedView ? <SortHeader k="margin">Marge réelle</SortHeader> : <span>Équipe</span>}
          <span />
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-10 text-gray-600">
            <Construction className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">
              {searchTerm ? 'Aucun chantier ne correspond à cette recherche' : `Aucun chantier « ${activeFilter.label.toLowerCase()} »`}
            </p>
          </div>
        ) : rows.map((project) => {
          const color = getProjectColor(project.id);
          return (
            <div key={project.id}
                 onClick={() => onEditProject(project)}
                 title="Ouvrir la fiche du chantier"
                 className={`relative grid grid-cols-1 ${GRID} gap-x-3 gap-y-1 items-center px-4 py-2.5 border-t border-gray-100 hover:bg-gray-50 cursor-pointer`}>
              <div className="flex items-center gap-2 min-w-0 pr-8 md:pr-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color.hex }} />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-gray-900 truncate">{project.name}</span>
                    {markers(project)}
                  </div>
                  {/* Le client n'apparaît que s'il apporte quelque chose : les
                      chantiers issus de Pennylane s'appellent déjà « Client — devis » */}
                  {project.clientName && !norm(project.name).startsWith(norm(project.clientName).trim()) && (
                    <div className="text-xs text-gray-500 truncate" title={project.clientAddress || ''}>
                      {project.clientName}
                    </div>
                  )}
                </div>
              </div>
              {/* Écran large : 4 colonnes. Téléphone : une ligne qui passe à la ligne. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm pl-4 md:pl-0 md:contents">
                <span><Badge className={`${STATUS_STYLE[project.status] || STATUS_STYLE.planned} font-normal`}>
                  {STATUS_LABEL[project.status] || project.status}
                </Badge></span>
                <span className="text-sm">{datesCell(project)}</span>
                <div className="text-sm min-w-[96px]">
                  {project.status === 'completed' ? gapCell(project) : hoursCell(project)}
                </div>
                <div className="text-sm min-w-0 flex">
                  {project.status === 'completed' ? marginCell(project) : teamCell(project)}
                </div>
              </div>
              <div className="absolute right-2 top-2 md:static md:flex md:justify-end">{rowMenu(project)}</div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1"><Pin className="w-3 h-3 text-red-500" /> date imposée</span>
        <span className="inline-flex items-center gap-1"><Hand className="w-3 h-3" /> posé à la main</span>
        <span className="inline-flex items-center gap-1"><FastForward className="w-3 h-3 text-blue-500" /> avancé avant son mois</span>
        <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-600" /> alerte (survol : cause)</span>
        <span>★ responsable</span>
      </p>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={deleteProjectId !== null} onOpenChange={() => setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le chantier</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce chantier ? Cette action est irréversible.
              {deleteProjectId && (
                <span className="block mt-2 font-medium">
                  {projects.find((p) => p.id === deleteProjectId)?.name}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ColorChoiceDialog
        project={colorChoiceProject}
        isOpen={colorChoiceProject !== null}
        onOpenChange={(open) => { if (!open) setColorChoiceProject(null); }}
      />

      <DocumentDialog
        project={documentsProject}
        isOpen={documentsProject !== null}
        onOpenChange={(open) => { if (!open) setDocumentsProject(null); }}
      />
    </div>
  );
};

export default AllProjectsView;
